import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// ============ Auth helpers ============
// (kept above the interceptors so they can call them)
export const getToken = () => localStorage.getItem('token');
export const setToken = (token) => localStorage.setItem('token', token);
export const clearToken = () => localStorage.removeItem('token');
export const getUser = () => {
  const u = localStorage.getItem('user');
  return u ? JSON.parse(u) : null;
};
export const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));
export const clearUser = () => localStorage.removeItem('user');

/**
 * Returns true if a token is present AND its `exp` claim is still in
 * the future. Decodes the JWT payload locally — no network call.
 *
 * Used by ProtectedRoute (route-time gate) AND the request interceptor
 * (request-time gate) so we never let an expired token leak into a
 * round-trip we already know will 401.
 */
export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export const logout = () => {
  clearToken();
  clearUser();
  window.location.href = '/admin/login';
};

// ---- 401 redirect deduplication ----
// Concurrent in-flight admin requests can ALL hit 401 if a token expired
// while idle. Without this guard each one would fire its own
// window.location.href + toast — we'd see the login page momentarily
// flicker and N copies of the "session expired" toast. The flag
// short-circuits everyone after the first to win the race.
let _redirectingToLogin = false;

const redirectToLoginOnce = (reason = 'expired') => {
  if (_redirectingToLogin) return;
  _redirectingToLogin = true;
  clearToken();
  clearUser();
  // Use replace so the protected page isn't in the back-button history
  // (avoids "back → still expired → bounce again" loops).
  window.location.replace(`/admin/login?expired=1&r=${reason}`);
};

const onAdminPath = () => {
  const p = window.location.pathname || '';
  return p.startsWith('/admin') && p !== '/admin/login';
};

// ============ Request interceptor ============
// 1. Attach JWT to outgoing requests.
// 2. Pre-empt 401: if we're on an admin page AND the token is already
//    expired, short-circuit the request and redirect cleanly. Saves a
//    pointless RTT and prevents in-flight requests with stale auth.
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    const url = (config.url || '');
    // Endpoints that legitimately MAY be called without auth — never
    // intercept them. Anything matching /admin/ on the SERVER paths
    // requires auth and is the place to enforce.
    const isLoginEndpoint = url.includes('/auth/login');

    if (token) {
      // Pre-empt: token in localStorage but already expired. Only enforce
      // this on admin-scoped pages so public funnel routes (which never
      // attach an admin token anyway) are unaffected.
      if (onAdminPath() && !isLoginEndpoint && !isAuthenticated()) {
        redirectToLoginOnce('preempt');
        // Reject so the caller's catch handler runs cleanly. We also
        // avoid sending a known-bad token over the wire.
        return Promise.reject(new axios.Cancel('Session expired (preempt)'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============ Response interceptor ============
// 1. Sliding refresh: if the server signals X-Refresh-Token, swap it
//    into localStorage. Every subsequent request uses the fresher token.
// 2. Handle 401s: clear auth + redirect to login (deduplicated).
api.interceptors.response.use(
  (response) => {
    try {
      const fresh = response?.headers?.['x-refresh-token'] || response?.headers?.['X-Refresh-Token'];
      if (fresh && typeof fresh === 'string' && fresh.split('.').length === 3) {
        setToken(fresh);
      }
    } catch (_) { /* never break a successful response */ }
    return response;
  },
  (error) => {
    // axios.Cancel from our preempt path — propagate quietly.
    if (axios.isCancel?.(error)) return Promise.reject(error);
    if (error?.response?.status === 401) {
      if (onAdminPath()) {
        redirectToLoginOnce('expired');
      }
    }
    return Promise.reject(error);
  },
);

// ============ API calls ============
export const login = (email, password) => api.post('/auth/login', { email, password });
export const refreshAuth = () => api.post('/auth/refresh');

export const uploadFiles = (files, leadId = '', labels = '') => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  if (leadId) formData.append('lead_id', leadId);
  if (labels) formData.append('labels', labels);
  return api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const submitIntake = (data) => api.post('/intake/submit', data);
export const getCustomerStatus = (token) => api.get(`/customer/status/${token}`);

// Image validation
export const validateImage = (file_id, property_type, requested_view) =>
  api.post('/files/validate-image', { file_id, property_type, requested_view });

// Phone verification (Twilio Verify + dev fallback)
export const sendOtp = (name, phone) => api.post('/verify/send', { name, phone });
export const checkOtp = (phone, code, name = '') =>
  api.post('/verify/check', { phone, code, name });
export const checkVerifiedSession = (token) =>
  api.get('/verify/session', { params: { token } });


// Admin
export const getLeads = (params = {}) => api.get('/admin/leads', { params });
export const getLeadDetail = (id) => api.get(`/admin/leads/${id}`);
export const updateLead = (id, data) => api.patch(`/admin/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/admin/leads/${id}`);

export const addOpening = (leadId, data) => api.post(`/admin/leads/${leadId}/openings`, data);
export const updateOpening = (id, data) => api.patch(`/admin/openings/${id}`, data);
export const deleteOpening = (id) => api.delete(`/admin/openings/${id}`);

export const updateFile = (id, data) => api.patch(`/files/${id}`, data);

export const createQuote = (data) => api.post('/admin/quotes', data);
export const updateQuote = (id, data) => api.patch(`/admin/quotes/${id}`, data);
export const getQuotePdf = (id) => {
  const token = getToken();
  return `${API}/admin/quotes/${id}/pdf?token=${token}`;
};

export const addActivity = (leadId, data) => api.post(`/admin/leads/${leadId}/activities`, data);

export const getManufacturers = () => api.get('/admin/manufacturers');
export const createManufacturer = (data) => api.post('/admin/manufacturers', data);
export const updateManufacturer = (id, data) => api.patch(`/admin/manufacturers/${id}`, data);
export const deleteManufacturer = (id) => api.delete(`/admin/manufacturers/${id}`);

export const getProductLines = (mfrId = '') => api.get('/admin/product-lines', { params: { manufacturer_id: mfrId } });
export const createProductLine = (data) => api.post('/admin/product-lines', data);
export const updateProductLine = (id, data) => api.patch(`/admin/product-lines/${id}`, data);
export const deleteProductLine = (id) => api.delete(`/admin/product-lines/${id}`);

export const getPricingTemplates = (mfrId = '') => api.get('/admin/pricing-templates', { params: { manufacturer_id: mfrId } });
export const createPricingTemplate = (data) => api.post('/admin/pricing-templates', data);
export const updatePricingTemplate = (id, data) => api.patch(`/admin/pricing-templates/${id}`, data);
export const deletePricingTemplate = (id) => api.delete(`/admin/pricing-templates/${id}`);

export const getStats = () => api.get('/admin/stats');
export const getUsers = () => api.get('/admin/users');
export const seedData = () => api.post('/seed');

export default api;
